// React
import { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// MUI components
import { Box, Typography, CircularProgress, Stack } from '@mui/material';

// Custom components
import UserAvatar from 'components/Avatar';
import CustomToolTip from 'components/Tooltip';
import InfiniteVirtualList from 'components/InfiniteVirtualList/InfiniteVirtualList';

// Utils
import xeniaApi from 'api/index';
import { useWorkspaceHook } from 'utils/CustomHooks/useWorkspaceHook';
import useMeasure from 'react-use-measure';
import { getTimeZone } from 'utils/globalFunction';

// Icons

import { HistoryContainer } from './styles';
import CustomModal from 'components/CustomModal';
import DIALOGS from 'utils/dialogIds';
import { useDispatch, useSelector } from 'react-redux';
import selectors from 'store/selectors';
import actions from 'store/actions';
import Header from './Header';
import useTemplateBuilder from 'utils/CustomHooks/templateBuilder/useTemplateBuilder';
import { generateTemplateHashes } from 'components/TaskChecklist/utils';
import HistoryItem from './historyItem';
import NoSubmission from 'public/assets/img/ImgNoSubmission.svg';

// Hooks
import { useWindowResize } from 'controller/useWindowResize';

interface HistoryItemInterface {
  afterEdit: Record<string, any>;
  beforeEdit: Record<string, any>;
  changesSummary: {
    itemsUpdated: number;
    notesChanged: number;
    flagsChanged: number;
  };
  editedAt: string;
  editedBy: string;
  itemsChanged: string[];
}

const SubmissionHistory: React.FC = () => {
  const dispatch = useDispatch();
  const dialogState = useSelector(selectors.getDialogState);
  const { organizeChecklistIntoLinear, organizeChecklistIntoHierarchy } =
    useTemplateBuilder();
  const [ref, { height }] = useMeasure();
  const dialogData = dialogState?.[DIALOGS.SUBMISSION_HISTORY_DIALOG]?.data;
  const open: boolean = dialogState?.[DIALOGS.SUBMISSION_HISTORY_DIALOG]?.open;

  const { submissionId } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();
  const { workspaceId, navigateWithWorkspaceUrl } = useWorkspaceHook();
  const tz = getTimeZone();

  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState<any>(null);
  const [history, setHistory] = useState<HistoryItemInterface[]>([]);
  const [itemsHash, setItemsHash] = useState<any>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const [page, setPage] = useState(1);

  const PAGE_SIZE = 10;

  // Fetch submission details and history
  useEffect(() => {
    if (submissionId) {
      fetchSubmissionHistory();
    }
  }, [submissionId, workspaceId]);

  const fetchSubmissionHistory = async (page = 1) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setIsNextPageLoading(true);
      }
      setPage(page);
      const logData = await xeniaApi.getChecklistLog({
        logId: submissionId,
        hotelId: workspaceId,
        sectioned: true,
        filtered: true,
      });

      const linearChecklistItems = organizeChecklistIntoLinear(logData?.data);
      const tempData = {
        ...logData?.data,
        TaskChecklistItems: linearChecklistItems,
      };

      const generatedTemplateLogHashes = generateTemplateHashes(
        linearChecklistItems,
        'id',
      );

      // Fetch history with pagination
      const historyData = await xeniaApi.getSubmissionHistory({
        logId: submissionId,
        page: page,
        limit: PAGE_SIZE,
      });

      if (page === 1) {
        setItemsHash(generatedTemplateLogHashes);
        setSubmission(tempData);
        setHistory(historyData.data || []);
      } else {
        setHistory((prev) => [...prev, ...(historyData.data || [])]);
      }

      setTotalCount(historyData?.totalCount || 0);
    } catch (err) {
      console.error('Error fetching submission history:', err);
    } finally {
      setLoading(false);
      setIsNextPageLoading(false);
    }
  };

  const handleLoadNextPage = async (offset: number) => {
    const nextPage = page + 1;
    await fetchSubmissionHistory(nextPage);
  };

  const handleClose = () => {
    // Navigate back to previous URL
    navigate(-1);
    dispatch(actions.closeDialog(DIALOGS.SUBMISSION_HISTORY_DIALOG));
  };

  // Create rows for virtualization
  const historyRows = useMemo(() => {
    return history.map((item, index) => (
      <HistoryItem
        key={`${item.editedAt}-${index}`}
        item={item}
        itemsHash={itemsHash}
      />
    ));
  }, [history, itemsHash]);

  const containerHeight = useMemo(() => {
    return `calc(100vh - ${height}px)`;
  }, [height]);

  return (
    <CustomModal
      disableEnforceFocus
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '0px',
          height: '100%',
          width: '100%',
          maxWidth: 'unset',
          margin: 0,
          maxHeight: 'unset',
        },
      }}
      fullWidth
      open={open}
      handleClose={handleClose}
    >
      <Stack
        sx={{
          background: '#F6F8FA',
          height: '100vh',
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              ref={ref}
              sx={{ position: 'fixed', top: 0, left: 0, zIndex: 99999999 }}
              width={'100%'}
            >
              <Header
                handleClickClose={handleClose}
                checklistLog={submission}
              />
            </Box>
            <HistoryContainer
              sx={{
                height: containerHeight,
                marginTop: `${height}px`,
              }}
            >
              {history.length ? (
                <InfiniteVirtualList
                  handleLoadNextPage={handleLoadNextPage}
                  rows={historyRows}
                  height={window.innerHeight - height - 50}
                  totalCount={totalCount}
                  isNextPageLoading={isNextPageLoading}
                  overrideRowCount={history?.length}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '400px',
                    textAlign: 'center',
                    margin: 'auto',
                  }}
                >
                  <img src={NoSubmission} />
                  <Typography
                    style={{
                      fontWeight: 800,
                      fontSize: '26px',
                      lineHeight: '27.32px',
                      marginTop: -15,
                      alignItems: 'center',
                      color: '#252350',
                      marginBottom: 10,
                    }}
                  >
                    No changes have been made
                  </Typography>
                  <Typography
                    style={{
                      fontWeight: 500,
                      fontSize: 14,
                      lineHeight: '22px',
                      letterSpacing: '1%',
                      color: '#000000',
                      marginBottom: 30,
                    }}
                  >
                    No changes have been recorded for this submission yet. Any
                    changes made will be displayed here.
                  </Typography>
                </Box>
              )}
            </HistoryContainer>
          </>
        )}
      </Stack>
    </CustomModal>
  );
};

export default SubmissionHistory;
